import { useCallback, useContext, useEffect, useImperativeHandle, useMemo, useRef, useState } from "react"
import styles from './index.module.scss'
import { forwardRef } from "react"
import raf from "rc-util/es/raf";
import _objectSpread from "@babel/runtime/helpers/esm/objectSpread2";
import useResizeObserver from "@/utils/useResizeObserver"
import { VideoContext } from "../../hooks/VideoContext";

function fixPoint(key, start, width, clientWidth) {
    const startAddWidth = start + width;
    const offsetStart = (width - clientWidth) / 2;
  
    if (width > clientWidth) {
      if (start > 0) {
        return {
          [key]: offsetStart,
        };
      }
      if (start < 0 && startAddWidth < clientWidth) {
        return {
          [key]: -offsetStart,
        };
      }
    } else if (start < 0 || startAddWidth > clientWidth) {
      return {
        [key]: start < 0 ? offsetStart : -offsetStart,
      };
    }
    return {};
  }
  
  /**
   * Fix positon x,y point when
   *
   * Ele width && height < client
   * - Back origin
   *
   * - Ele width | height > clientWidth | clientHeight
   * - left | top > 0 -> Back 0
   * - left | top + width | height < clientWidth | clientHeight -> Back left | top + width | height === clientWidth | clientHeight
   *
   * Regardless of other
   */
function getFixScaleEleTransPosition(
    width,
    height,
    left,
    top,
    parentWidth,
    parentHeight
) {
    let fixPos = null;

    if (width <= parentWidth && height <= parentHeight) {
        fixPos = {
        x: 0,
        y: 0,
        };
    } else if (width > parentWidth || height > parentHeight) {
        fixPos = {
        ...fixPoint('x', left, width, parentWidth),
        ...fixPoint('y', top, height, parentHeight),
        };
    }

    return fixPos;
}

const DEFAULT_TRANSFORM = { x: 0, y: 0, scale: 1 }
const VIDEO_SYNC_TOLERANCE_SECONDS = 0.05

const ImageRender = forwardRef((props, ref) => {
    const { onTransform, onLoad, src, initialTransform, onVideoEvent } = props
    const initialTransformRef = useRef(normalizeTransform(initialTransform))
    const [transform, setTransform] = useState(initialTransformRef.current)
    const transformRef = useRef(initialTransformRef.current)
    const onTransformRef = useRef(onTransform)
    const onLoadRef = useRef(onLoad)
    const onVideoEventRef = useRef(onVideoEvent)
    // 标记下一次对应媒体事件由同步命令产生，事件上报后立即消费，避免反向触发源视频。
    const suppressedVideoEventsRef = useRef({ seek: false, play: false, pause: false })
    // 目标视频元数据尚未加载时暂存同步时间，待 duration 可用后再安全写入。
    const pendingVideoTimeRef = useRef(null)
    const minScale = 0.5
    const maxScale = 100

    useEffect(() => {
        onTransformRef.current = onTransform
    }, [onTransform])

    useEffect(() => {
        onLoadRef.current = onLoad
    }, [onLoad])

    useEffect(() => {
        onVideoEventRef.current = onVideoEvent
    }, [onVideoEvent])

    const markVideoEventAsSynced = useCallback((type) => {
        suppressedVideoEventsRef.current[type] = true
    }, [])

    const getVideoEventSource = useCallback((type) => {
        if (!suppressedVideoEventsRef.current[type]) return 'user'
        suppressedVideoEventsRef.current[type] = false
        return 'sync'
    }, [])

    const applySyncedVideoTime = useCallback((video, currentTime) => {
        if (!Number.isFinite(currentTime)) return
        if (video.readyState < 1 || !Number.isFinite(video.duration) || video.duration <= 0) {
            pendingVideoTimeRef.current = Math.max(0, currentTime)
            return
        }

        pendingVideoTimeRef.current = null
        const targetTime = Math.max(0, Math.min(currentTime, video.duration))
        if (Math.abs(video.currentTime - targetTime) <= VIDEO_SYNC_TOLERANCE_SECONDS) return

        markVideoEventAsSynced('seek')
        try {
            video.currentTime = targetTime
        } catch (error) {
            getVideoEventSource('seek')
            console.warn('同步视频进度失败:', error)
        }
    }, [getVideoEventSource, markVideoEventAsSynced])

    useEffect(() => {
        onTransformRef.current &&
        onTransformRef.current(initialTransformRef.current, { source: 'init' })
    }, [])

    const style = useMemo(() => {
        return {
            transform: `translate3d(${transform.x}px, ${transform.y}px, 0px) scale3d(${transform.scale}, ${transform.scale}, 1) rotate(0deg)`,
            userSelect: "none",
        }
    }, [transform])

    const imgRef = useResizeObserver((entries) => {
        for (let entry of entries) {
            // 处理尺寸变化的逻辑
            onLoadRef.current &&
            onLoadRef.current({
                clientWidth: entry.contentRect.width,
                clientHeight: entry.contentRect.height
            })
        }
    })
    const frame = useRef(null);
    const queue = useRef([]);
    useEffect(() => {
        return () => {
            if (frame.current !== null) {
                raf.cancel(frame.current);
                frame.current = null;
            }
            queue.current = [];
        }
    }, [])
    /** Direct update transform */
    var updateTransform = function updateTransform(newTransform, meta = {}) {
        if (frame.current === null) {
            frame.current = raf(function () {
                setTransform(function () {
                    var memoState = transformRef.current;
                    var lastMeta = { source: 'unknown' }
                    queue.current.forEach(function (queueItem) {
                        memoState = queueItem?.state || memoState
                        lastMeta = queueItem?.meta || lastMeta
                    });
                    queue.current = [];
                    frame.current = null;
                    transformRef.current = memoState;
                    onTransformRef.current === null || onTransformRef.current === void 0 || onTransformRef.current(memoState, lastMeta);
                    return memoState;
                });
            });
        }
        const baseTransform = queue.current.length
            ? (queue.current[queue.current.length - 1]?.state || transformRef.current)
            : transformRef.current
        queue.current.push({
            state: _objectSpread(_objectSpread({}, baseTransform), newTransform),
            meta,
        });
    };

    useImperativeHandle(ref, () => ({
        getBoundingClientRect: () => {
            return imgRef.current.getBoundingClientRect()
        },
        getTransform: () => transformRef.current,
        setTransform: (nextTransform, nextMeta = {}) => {
            updateTransform(nextTransform, nextMeta)
        },
        resetTransform: (nextMeta = {}) => {
            updateTransform(DEFAULT_TRANSFORM, nextMeta)
        },
        isTransformed: () => (
            transformRef.current.x !== 0 || transformRef.current.y !== 0 || transformRef.current.scale !== 1
        ),
        onWheel,
        onMouseMove,
        onMouseUp,
        getVideoState: () => {
            if (!isVideo || !imgRef.current) return null
            return {
                currentTime: imgRef.current.currentTime || 0,
                isPlaying: !imgRef.current.paused,
            }
        },
        // 由同步锁调用；先标记再操作 DOM，供媒体事件监听器区分用户操作和同步操作。
        syncVideo: ({ type, currentTime }) => {
            if (!isVideo || !imgRef.current) return
            const video = imgRef.current
            applySyncedVideoTime(video, currentTime)
            if (type === 'play' && video.paused) {
                markVideoEventAsSynced('play')
                video.play().catch(error => {
                    getVideoEventSource('play')
                    console.warn('同步视频播放失败:', error)
                })
            }
            if (type === 'pause' && !video.paused) {
                markVideoEventAsSynced('pause')
                video.pause()
            }
        },
    }))
    const dispatchZoomChange = (ratio, centerX, centerY) => {
        const activeTransform = transformRef.current
        const _imgRef$current = imgRef.current,
            offsetWidth = _imgRef$current.offsetWidth,
            offsetHeight = _imgRef$current.offsetHeight,
            offsetLeft = _imgRef$current.offsetLeft,
            offsetTop = _imgRef$current.offsetTop;

        let newRatio = ratio;
        let newScale = activeTransform.scale * ratio;
        if (newScale > maxScale) {
            newScale = maxScale;
            newRatio = maxScale /  activeTransform.scale;
        } else if (newScale < minScale) {
            // For mobile interactions, allow scaling down to the minimum scale.
            newScale = minScale;
            newRatio = newScale / activeTransform.scale;
        }

        /** Default center point scaling */
        var mergedCenterX = centerX
        var mergedCenterY = centerY
        var diffRatio = newRatio - 1;
        /** Deviation calculated from image size */
        var diffImgX = diffRatio * offsetWidth * 0.5;
        var diffImgY = diffRatio * offsetHeight * 0.5;
        /** The difference between the click position and the edge of the document */
        var diffOffsetLeft = diffRatio * (mergedCenterX - activeTransform.x - offsetLeft);
        var diffOffsetTop = diffRatio * (mergedCenterY - activeTransform.y - offsetTop);
        /** Final positioning */
        let newX = activeTransform.x - (diffOffsetLeft - diffImgX);
        let newY = activeTransform.y - (diffOffsetTop - diffImgY);

        /**
         * When zooming the image
         * When the image size is smaller than the width and height of the window, the position is initialized
         */
        if (ratio < 1 && newScale === 1) {
            const mergedWidth = offsetWidth * newScale;
            const mergedHeight = offsetHeight * newScale;
            const clientWidth = document.documentElement.clientWidth
            const clientHeight = document.documentElement.clientHeight;
            if (mergedWidth <= clientWidth && mergedHeight <= clientHeight) {
                newX = 0;
                newY = 0;
            }
        }
        updateTransform({x: newX, y: newY, scale: newScale}, { source: 'user' })
    }

    const onWheel = (e) => {
        if (e.deltaY == 0) return
        const scaleStep = 0.2
        // Scale ratio depends on the deltaY size
        const scaleRatio = Math.abs(e.deltaY / 100)
        // Limit the maximum scale ratio
        const mergedScaleRatio = Math.min(scaleRatio, 1)
        // Scale the ratio each time
        let ratio = 1 + mergedScaleRatio * scaleStep
        if (e.deltaY > 0) {
            ratio = 1 / ratio
        }
        const boundRect = imgRef.current.parentNode.getBoundingClientRect()
        dispatchZoomChange(ratio, e.clientX - boundRect.left, e.clientY)
    }

    const [isMoving, setMoving] = useState(false);
    const startPositionInfo = useRef({
        diffX: 0,
        diffY: 0,
        transformX: 0,
        transformY: 0,
    });
    const onMouseDown = (e) => {
        if (e.button !== 0) return;
        e.preventDefault();
        e.stopPropagation();
        const activeTransform = transformRef.current
        startPositionInfo.current = {
            diffX: e.pageX - activeTransform.x,
            diffY: e.pageY - activeTransform.y,
            transformX: activeTransform.x,
            transformY: activeTransform.y,
        };
        setMoving(true);
    }
    const onMouseMove = (e) => {
        if (isMoving) {
            updateTransform({
                x: e.pageX - startPositionInfo.current.diffX,
                y: e.pageY - startPositionInfo.current.diffY,
            }, { source: 'user' })
        }
    }
    const onMouseUp = (e) => {
        if (isMoving) {
            setMoving(false)
            const activeTransform = transformRef.current
            /** No need to restore the position when the picture is not moved, So as not to interfere with the click */
            const { transformX, transformY } = startPositionInfo.current;
            const hasChangedPosition = activeTransform.x !== transformX || activeTransform.y !== transformY;
            if (!hasChangedPosition) return;

            const parentNode = imgRef.current.offsetParent

            const width = imgRef.current.offsetWidth * activeTransform.scale;
            const height = imgRef.current.offsetHeight * activeTransform.scale;

            const parentWidth = parentNode.offsetWidth
            const parentHeight = parentNode.offsetHeight


            const { left, top } = imgRef.current.getBoundingClientRect();
            const { parentLeft, parentTop } = parentNode.getBoundingClientRect();

            const fixState = getFixScaleEleTransPosition(
                width,
                height,
                left - parentLeft,
                top - parentTop,
                parentWidth,
                parentHeight
            );

            if (fixState) {
                updateTransform({ ...fixState }, { source: 'user' });
            }
        }
    }
    const handleLoad = (e) => {
        onLoadRef.current &&
        onLoadRef.current(e.target)
    }

    // 视频播放相关
    const {
        videoRef,
        setIsLoading,
        setIsPlaying,
        setCurrentTime,
        setDuration,
        isMuted,
    } = useContext(VideoContext)
    const isVideo = useMemo(() => {
        return isVideoUrl(src)
    }, [src])
    useEffect(() => {
        if (!isVideo) return
        videoRef.current = imgRef.current
        const video = imgRef.current
        const handleTimeUpdate = () => {
            setCurrentTime(video.currentTime)
            onVideoEventRef.current?.({ type: 'timeupdate', currentTime: video.currentTime })
        }
        const handleSeeking = () => {
            // 进度条跳转不能只等待 timeupdate；seeking 触发时立即将目标视频定位到相同帧。
            onVideoEventRef.current?.({
                type: 'seek',
                currentTime: video.currentTime,
                source: getVideoEventSource('seek'),
            })
        }
        const handlePlay = () => {
            setIsPlaying(true)
            setIsLoading(false);
            onVideoEventRef.current?.({
                type: 'play',
                currentTime: video.currentTime,
                source: getVideoEventSource('play'),
            })
        }
        const handlePause = () => {
            setIsPlaying(false)
            onVideoEventRef.current?.({
                type: 'pause',
                currentTime: video.currentTime,
                source: getVideoEventSource('pause'),
            })
        }
        const handleLoadedMetadata = () => {
            setDuration(video.duration)
            if (pendingVideoTimeRef.current !== null) {
                applySyncedVideoTime(video, pendingVideoTimeRef.current)
            }
        }
        const handleWaiting = () => {
            setIsLoading(true)
        }
        const handleCanPlay = () => {
            setIsLoading(false)
        }
        const handleEnded = () => {
            setIsPlaying(false);
            setCurrentTime(0);
        };
        // 添加事件监听器
        video.addEventListener('timeupdate', handleTimeUpdate);
        video.addEventListener('seeking', handleSeeking);
        video.addEventListener('play', handlePlay);
        video.addEventListener('pause', handlePause);
        video.addEventListener('loadedmetadata', handleLoadedMetadata);
        video.addEventListener('waiting', handleWaiting);
        video.addEventListener('canplay', handleCanPlay);
        video.addEventListener('ended', handleEnded);
        // 清理函数
        return () => {
            video.removeEventListener('timeupdate', handleTimeUpdate);
            video.removeEventListener('seeking', handleSeeking);
            video.removeEventListener('play', handlePlay);
            video.removeEventListener('pause', handlePause);
            video.removeEventListener('loadedmetadata', handleLoadedMetadata);
            video.removeEventListener('waiting', handleWaiting);
            video.removeEventListener('canplay', handleCanPlay);
            video.removeEventListener('ended', handleEnded);
        }
    }, [applySyncedVideoTime, getVideoEventSource, imgRef, isVideo, setCurrentTime, setDuration, setIsLoading, setIsPlaying, videoRef])

    useEffect(() => {
        if (isVideo && imgRef.current) imgRef.current.muted = isMuted
    }, [imgRef, isMuted, isVideo])

    return (
        isVideo ?
        <video
            ref={imgRef}
            className={styles.ImageRender}
            style={style}
            src={src}
            onWheel={onWheel}
            onMouseDown={onMouseDown}
            onMouseMove={onMouseMove}
            onMouseUp={onMouseUp}
            onLoad={handleLoad}
            autoPlay
            loop
        /> : <img
            ref={imgRef}
            className={styles.ImageRender}
            style={style}
            src={src}
            alt=""
            loading="eager"
            onWheel={onWheel}
            onMouseDown={onMouseDown}
            onMouseMove={onMouseMove}
            onMouseUp={onMouseUp}
            onLoad={handleLoad}
        />
    )
})

export default ImageRender

// 常见视频扩展名，用于判断 url 是否为视频资源
const VIDEO_EXTENSIONS = /\.(mp4|webm|ogg|mov|avi|mkv|flv|wmv|m4v)(\?|$)/i

/** 根据 url 扩展名判断是否为视频 */
function isVideoUrl(url) {
    if (!url) return false
    return VIDEO_EXTENSIONS.test(url)
}

function normalizeTransform(transform) {
    return {
        x: transform?.x || 0,
        y: transform?.y || 0,
        scale: transform?.scale || 1,
    }
}
